import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { DollarSign, FileText, TrendingUp, TrendingDown, AlertCircle, Plus, ArrowRight, Receipt, Landmark, Wallet } from 'lucide-react';
import { fmt, computeKPIs } from '../../lib/accountingStore';
import { apiInvoices, apiCustomers, apiExpenses, apiAccounts, apiTransactions, apiVendors, apiBills } from '../../lib/api';

const MoneyBar = ({ income, expenses }) => {
  const total = income + expenses || 1;
  const iPct = (income/total)*100;
  return (
    <div>
      <div className="flex h-8 rounded-lg overflow-hidden">
        <div className="bg-emerald-500" style={{width:`${iPct}%`}}/>
        <div className="bg-red-400" style={{width:`${100-iPct}%`}}/>
      </div>
      <div className="flex justify-between text-xs mt-2">
        <div><span className="inline-block w-2 h-2 bg-emerald-500 rounded-full mr-1.5"/>Income {fmt(income)}</div>
        <div><span className="inline-block w-2 h-2 bg-red-400 rounded-full mr-1.5"/>Expenses {fmt(expenses)}</div>
      </div>
    </div>
  );
};

export default function AcctDashboard() {
  const [s, setS] = useState({ invoices: [], customers: [], expenses: [], accounts: [], transactions: [], vendors: [], bills: [] });
  useEffect(() => {
    Promise.all([apiInvoices.list(), apiCustomers.list(), apiExpenses.list(), apiAccounts.list(), apiTransactions.list(), apiVendors.list(), apiBills.list()]).then(([inv, cust, exp, acc, tx, ven, bil]) => {
      setS({ invoices: inv || [], customers: cust || [], expenses: exp || [], accounts: acc || [], transactions: tx || [], vendors: ven || [], bills: bil || [] });
    }).catch(() => {});
  }, []);
  const k = computeKPIs(s);
  const cards = [
    { l: 'Income (MTD)', v: fmt(k.income), i: TrendingUp, c: 'text-emerald-600 bg-emerald-50', delta: '+12.4%' },
    { l: 'Expenses (MTD)', v: fmt(k.expenses), i: TrendingDown, c: 'text-red-600 bg-red-50', delta: '+3.2%' },
    { l: 'Net Profit', v: fmt(k.profit), i: DollarSign, c: 'text-blue-600 bg-blue-50', delta: '+9.1%' },
    { l: 'Open A/R', v: fmt(k.ar), i: FileText, c: 'text-purple-600 bg-purple-50', delta: `${s.invoices.filter(i=>i.status!=='Paid').length} invoices` },
  ];
  const bank = s.accounts.filter(a => a.type === 'Bank');
  const ccard = s.accounts.find(a => a.type === 'Credit Card');
  const recentInv = s.invoices.slice(-5).reverse();
  const recentExp = s.expenses.slice(-5).reverse();

  return (
    <div className="p-6 lg:p-8 space-y-5">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map(c => (
          <Card key={c.l} className="pp-lift p-5 border-slate-200">
            <div className="flex items-start justify-between">
              <div>
                <div className="text-sm text-slate-500">{c.l}</div>
                <div className="text-3xl font-extrabold text-slate-900 mt-1">{c.v}</div>
                <div className="text-xs mt-1.5 text-slate-500 font-medium">{c.delta}</div>
              </div>
              <div className={`w-11 h-11 rounded-lg ${c.c} flex items-center justify-center`}><c.i className="w-5 h-5"/></div>
            </div>
          </Card>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        <Card className="lg:col-span-2 p-6 border-slate-200">
          <div className="flex justify-between items-start mb-4">
            <div><h3 className="font-bold text-slate-900">Profit &amp; Loss – This Month</h3><p className="text-sm text-slate-500">{fmt(k.income)} income − {fmt(k.expenses)} expenses</p></div>
            <Button asChild variant="outline" size="sm"><Link to="/app/accounting/reports">View Reports</Link></Button>
          </div>
          <MoneyBar income={k.income} expenses={k.expenses}/>
          <div className="grid grid-cols-3 gap-4 mt-6 pt-6 border-t border-slate-100">
            <div><div className="text-xs text-slate-500">Gross Income</div><div className="font-bold text-lg text-slate-900">{fmt(k.income)}</div></div>
            <div><div className="text-xs text-slate-500">Total Expenses</div><div className="font-bold text-lg text-slate-900">{fmt(k.expenses)}</div></div>
            <div><div className="text-xs text-slate-500">Net Profit</div><div className={`font-bold text-lg ${k.profit>=0?'text-emerald-600':'text-red-600'}`}>{fmt(k.profit)}</div></div>
          </div>
        </Card>
        <Card className="p-6 border-slate-200">
          <h3 className="font-bold text-slate-900">Account Balances</h3>
          <div className="mt-4 space-y-3">
            {bank.map(b => (
              <div key={b.id} className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center"><Landmark className="w-4 h-4"/></div>
                <div className="flex-1 min-w-0"><div className="text-sm font-semibold text-slate-900 truncate">{b.name}</div><div className="text-xs text-slate-500">{b.type}</div></div>
                <div className="text-sm font-bold text-slate-900 tabular-nums">{fmt(b.balance)}</div>
              </div>
            ))}
            {ccard && (
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-orange-50 text-orange-600 flex items-center justify-center"><Wallet className="w-4 h-4"/></div>
                <div className="flex-1 min-w-0"><div className="text-sm font-semibold text-slate-900 truncate">{ccard.name}</div><div className="text-xs text-slate-500">{ccard.type}</div></div>
                <div className="text-sm font-bold text-red-600 tabular-nums">−{fmt(ccard.balance)}</div>
              </div>
            )}
          </div>
          <Button asChild className="mt-5 w-full bg-[#8BC53F] hover:bg-[#6FA82C]"><Link to="/app/accounting/banking">Open Banking</Link></Button>
        </Card>
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        <Card className="p-6 border-slate-200">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-bold text-slate-900">Recent Invoices</h3>
            <Link to="/app/accounting/invoices" className="text-sm font-semibold text-[#6FA82C] hover:underline">View all →</Link>
          </div>
          <div className="space-y-2">
            {recentInv.map(inv => {
              const cust = s.customers.find(c=>c.id===inv.customerId);
              const statusC = inv.status==='Paid'?'bg-emerald-100 text-emerald-700':inv.status==='Overdue'?'bg-red-100 text-red-700':'bg-slate-100 text-slate-700';
              return (
                <div key={inv.id} className="flex items-center justify-between p-2.5 rounded-lg hover:bg-slate-50">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-slate-900 truncate">{cust?.name || 'Unknown'}</div>
                    <div className="text-xs text-slate-500">{inv.id} · {inv.date}</div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge className={`${statusC} border-0 text-[10px] font-bold`}>{inv.status.toUpperCase()}</Badge>
                    <div className="font-bold text-slate-900 tabular-nums w-20 text-right">{fmt(inv.total)}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
        <Card className="p-6 border-slate-200">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-bold text-slate-900">Recent Expenses</h3>
            <Link to="/app/accounting/expenses" className="text-sm font-semibold text-[#6FA82C] hover:underline">View all →</Link>
          </div>
          <div className="space-y-2">
            {recentExp.map(e => {
              const v = s.vendors.find(x=>x.id===e.vendorId);
              return (
                <div key={e.id} className="flex items-center justify-between p-2.5 rounded-lg hover:bg-slate-50">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-slate-900 truncate">{v?.name || e.memo}</div>
                    <div className="text-xs text-slate-500">{e.category} · {e.date}</div>
                  </div>
                  <div className="font-bold text-red-600 tabular-nums">−{fmt(e.amount)}</div>
                </div>
              );
            })}
          </div>
        </Card>
      </div>

      {k.overdue > 0 && (
        <Card className="p-5 border-red-200 bg-red-50 flex items-center gap-4">
          <div className="w-11 h-11 rounded-lg bg-red-100 text-red-600 flex items-center justify-center"><AlertCircle className="w-5 h-5"/></div>
          <div className="flex-1">
            <div className="font-bold text-red-900">{fmt(k.overdue)} overdue across {s.invoices.filter(i=>i.status==='Overdue').length} invoice(s)</div>
            <div className="text-sm text-red-700">Send a reminder or call your customer.</div>
          </div>
          <Button asChild className="bg-red-600 hover:bg-red-700"><Link to="/app/accounting/invoices">Review</Link></Button>
        </Card>
      )}
    </div>
  );
}
